"use client";

import React, { useState, useRef } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import { UploadCloud, Camera } from "lucide-react";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import { useUpdateUserDoc } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { useSearchParams } from "next/navigation";

interface ImageCropperProps {
  image: string | null;
  setImage: (url: string | null) => void;
  docTypeId: number;
  label: string;
  className?: string;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  image,
  setImage,
  docTypeId,
  label,
  className
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({ unit: "%", width: 50, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [showModal, setShowModal] = useState(false);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { user: authUser } = useAuth();
  const searchParams = useSearchParams();
  const user_id = searchParams.get("user_id");
  const path = searchParams.get("type");
  const userId = path ? user_id : authUser?.id ?? -1;

  const b2bUrl = process.env.NEXT_PUBLIC_B2B_URL;


  const { mutate: uploadDoc } = useUpdateUserDoc(Number(userId));


  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const reader = new FileReader();
      reader.onload = () => {
        setSrc(reader.result as string);
        setShowModal(true);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
  };

  //   const cropImage = () => {
  //     if (
  //       !completedCrop ||
  //       !imgRef.current ||
  //       !canvasRef.current ||
  //       !completedCrop.width ||
  //       !completedCrop.height
  //     )
  //       return;

  //     const image = imgRef.current;
  //     const canvas = canvasRef.current;
  //     const scaleX = image.naturalWidth / image.width;
  //     const scaleY = image.naturalHeight / image.height;

  //     canvas.width = completedCrop.width;
  //     canvas.height = completedCrop.height;

  //     const ctx = canvas.getContext("2d");
  //     if (!ctx) return;

  //     ctx.drawImage(
  //       image,
  //       completedCrop.x * scaleX,
  //       completedCrop.y * scaleY,
  //       completedCrop.width * scaleX,
  //       completedCrop.height * scaleY,
  //       0,
  //       0,
  //       completedCrop.width,
  //       completedCrop.height
  //     );

  //     canvas.toBlob((blob) => {
  //       if (blob) {
  //         const url = URL.createObjectURL(blob);
  //         setImage(url);
  //         setSrc(null);
  //         setShowModal(false);
  //       }
  //     }, "image/jpeg");
  //   };

  const cropImage = () => {
    if (
      !completedCrop ||
      !imgRef.current ||
      !canvasRef.current ||
      !completedCrop.width ||
      !completedCrop.height
    )
      return;

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });


        try {
          // 👇 Call your existing uploadDoc API here
          const response = await uploadDoc(
            {
              file,
              folder: "profile",
              doc_type_id: String(docTypeId),
              user_id: String(userId),
            },
            {
              onSuccess: (response: any) => {
                const uploadedUrl = response?.uploadData?.doc_file_upload;
                // return;
                if (uploadedUrl) {
                  setImage(`${uploadedUrl}`);
                  setSrc(null);
                  setShowModal(false);
                }
              },
              onError: () => {
                toast.error("Image upload failed");
              },
            }
          );

          // const uploadedUrl = response?.uploadData?.doc_file_upload;
          // if (uploadedUrl) {
          //   setImage(uploadedUrl); // 👈 This will be the real uploaded URL
          // }
        } catch (error) {
          toast.error("Image upload failed");
        }

        setShowModal(false);
        setSrc(null);
      }
    }, "image/jpeg");
  };

  return (
    <>
      {/* Styled Upload UI */}
      <div className="flex flex-col items-center space-y-3 ">
        <div className="relative group ">
          <label className="cursor-pointer ">
            <input
              type="file"
              onChange={onSelectFile}
              className="hidden"
              accept="image/*"
            />
            <div className={`relative  ${className}`}>
              {image ? (
                <img
                  src={`${b2bUrl}${image}`}
                  className={`w-35 h-30 object-contain border-4 border-white shadow-md transition-all duration-300 text-[10px]  ${className}`}
                  alt={label}
                />
              ) : (
                <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                  <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5  shadow-md hover:shadow-md transition-shadow rounded-full">
                <Camera className="w-4 h-4" />
              </div>
            </div>
          </label>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-medium text-gray-700">{label}</p>
          <p className="text-xs text-gray-500">Click to upload or change</p>
        </div>
      </div>

      {/* Hidden canvas */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Modal Cropper */}
      {showModal && src && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div
            className="bg-white p-4 rounded shadow-lg w-full max-w-md h-[550px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-2">Crop Image</h2>

            <div className="max-w-[300px] mx-auto">
              <ReactCrop
                crop={crop}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
              >
                <img
                  src={src}
                  onLoad={(e) => onImageLoaded(e.currentTarget)}
                  className="max-h-[200px] max-w-[200px] w-[300px] object-contain mx-auto"
                />
              </ReactCrop>
            </div>

            <div className="mt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setSrc(null);
                  setShowModal(false);
                }}
                className="px-4 py-1 text-sm bg-gray-300 hover:bg-gray-400 rounded text-[10px] cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={cropImage}
                className="px-4 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] cursor-pointer"
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCropper;
